/*
 * @Description: upload modal
 * @Author: Jack Deng
 * @Date: 2021-03-09 15:05:13
 * @LastEditors: Teemo Ding
 * @LastEditTime: 2022-03-17 16:39:09
 * @FilePath: /unifyxp-v2-store-portal/src/components/Upload/UploadModal/index.tsx
 */
import { flatten } from 'lodash';
import React from 'react';
import { Spin } from 'antd';

import useService from './service';
import Warning from '@/src/components/Antd/Modal/WarningModal';
import { Modal, Upload, Button, CircleTags } from '@/src/components/Antd';
import { Filter } from '@/src/components';
import { transSizeToKB } from '@/src/tools/helper';
import { PictureCard } from '../index';
import { ImageFile } from '../ImageDisplay/imageDisplay';
import { filterConfig } from './config';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet, replaceI18nVariables } from 'src/tools/tools';

import styles from './style.module.less';

type Props = {
  type?: 'Image' | 'Video' | 'Both';
  width: number;
  visible: boolean;
  onCancel: () => void;
  onOk: (list: Array<ImageFile>) => void;
  size?: number;
  fileList?: Array<ImageFile>;
};
const typeVideo = 'video/mp4,video/mov';
const typeImage = 'image/*';
const typeBoth = `${typeVideo},${typeImage}`;

function UploadModal({
  type: uploadType,
  size,
  visible,
  onCancel,
  width,
  onOk,
}: Props) {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);

  const {
    map,
    warningVisible,
    fileList,
    searchValue,
    selected,
    type,
    tags,
    selectedTag,
    loading,
    saveLoading,
    reset,
    hideWarning,
    showWarning,
    handleScroll,
    handleSearch,
    handleSubmit,
    handleDeleteType,
    handleDeleteTags,
    handleClickFilter,
    handleClearSelect,
    handleClickCard,
    handleChangeFileList,
    handleSearchValueChange,
  } = useService(visible, uploadType, size);
  console.log('selected', selected);

  const selectedFile =
    Array.isArray(selected) && selected.length === 1
      ? fileList.find((f) => f.uuid === selected[0])
      : {};

  const selectedListTags = flatten(
    selected.map((id) => {
      const file = fileList.find((f) => f.uuid === id);
      return file && file.tags ? file.tags : [];
    }),
  );
  // console.log('selectedListTags', selectedListTags);

  const accept = {
    Image: typeImage,
    Video: typeVideo,
    Both: typeBoth,
  };
  return (
    <Modal
      centered
      className={styles.modal}
      width={width}
      visible={visible}
      title={
        <div className={styles.title}>{getI18n('general.selectImage')}</div>
      }
      destroyOnClose
      afterClose={reset}
      maskClosable={!fileList.some((f) => f.status === 'uploading')}
      footer={null}
      onCancel={() =>
        fileList.some((f) => f.status === 'uploading')
          ? showWarning()
          : onCancel()
      }>
      <div className={styles.container}>
        <div>{JSON.stringify(scroll)}</div>
        <div className={styles.content}>
          <div className={styles.left}>
            <div className={styles.search}>
              <Filter
                searchValue={searchValue}
                searchOnchange={handleSearchValueChange}
                pressEnter={handleSearch}
                onSearch={handleSearch}
                overlayClick={handleClickFilter}
                filterList={filterConfig(
                  tags.map((t) => ({ key: t.uuid, name: t.name })),
                  { getI18n },
                  uploadType,
                )}
              />
              <div className={styles.tags}>
                {type && (
                  <CircleTags closable onClose={handleDeleteType}>
                    {type}
                  </CircleTags>
                )}
                {selectedTag && (
                  <CircleTags
                    key={selectedTag.key}
                    closable
                    onClose={handleDeleteTags}>
                    {selectedTag.name}
                  </CircleTags>
                )}
              </div>
            </div>
            <div className={styles.upload}>
              <Upload
                accept={accept[uploadType]}
                drag={true}
                multiple
                fileList={fileList}
                onChangeStatus={handleChangeFileList}
                showUploadList={false}>
                <div className={styles.uploadContent}>
                  <div className={styles.uploadTitle}>
                    {getI18n('general.draganddropfilehereor')}
                    <Button>{getI18n('general.browse')}</Button>
                  </div>
                  <div className={styles.uploadDesc}>
                    ({getI18n('general.allowedfiletypes')}: jpeg, gif, png)
                  </div>
                </div>
              </Upload>
            </div>
            <Spin spinning={loading}>
              <div className={styles.uploadList} onScroll={handleScroll}>
                {fileList.map((file, index) => {
                  return (
                    <PictureCard
                      key={file.uuid || file.uid}
                      checked={selected.indexOf(file.uuid) !== -1}
                      loading={file.status === 'uploading'}
                      percent={file.percent}
                      file={file}
                      onClick={
                        file.status === 'uploading'
                          ? () => {
                              return;
                            }
                          : handleClickCard //loading时不能选择
                      }
                    />
                  );
                })}
              </div>
            </Spin>
          </div>
          <div className={styles.right}>
            <div className={styles.title}>
              {getI18n('general.imageDetails')}
            </div>
            {selected.length === 1 && (
              <div className={styles.preview}>
                <div
                  className={styles.image}
                  style={{
                    backgroundImage: `url('${(selectedFile || {}).url}')`,
                  }}
                />
                <div className={styles.imageName}>
                  {`${(selectedFile || {}).originalName}`}
                </div>
                <div className={styles.imageDate}>
                  {replaceI18nVariables(
                    getI18n('general.upload.EditModal.uploaded'),
                    new Date((selectedFile || {}).createdDate).toDateString(),
                  )}
                </div>
                <div className={styles.imageSize}>
                  {replaceI18nVariables(
                    getI18n('general.upload.EditModal.size'),
                    transSizeToKB((selectedFile || {}).size),
                  )}
                </div>
                <div className={styles.imageD}>
                  {replaceI18nVariables(
                    getI18n('general.upload.EditModal.dimension'),
                    (selectedFile || {}).width,
                    (selectedFile || {}).height,
                  )}
                </div>
              </div>
            )}
            {selected.length > 1 && (
              <div className={styles.previewTags}>
                <span className={styles.title}>{`${selected.length} ${getI18n(
                  'general.imagesselected',
                )}`}</span>
                <div className={styles.previewTagsList}>
                  {selectedListTags.map((tag) => (
                    <CircleTags>{tag.name}</CircleTags>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <div className={styles.length}>{`${selected.length} ${getI18n(
              'general.itemsselected',
            )}`}</div>
            <div className={styles.clear} onClick={handleClearSelect}>
              {getI18n('general.clearSelection')}
            </div>
          </div>
          <div className={styles.footerMiddle}>
            {selected.map((item) => {
              const url = map.get(item);
              return (
                <div
                  key={item}
                  className={styles.image}
                  style={{
                    backgroundImage: `url('${url}')`,
                  }}
                />
              );
            })}
          </div>
          <div className={styles.footerRight}>
            <Button
              type='primary'
              onClick={handleSubmit.bind(this, onOk)}
              disabled={
                fileList.some((f) => f.status === 'uploading') ||
                (size === 1 && (selected || []).length === 0)
              }
              loading={saveLoading}>
              {getI18n('general.insert')}
            </Button>
          </div>
          <Warning
            visible={Boolean(warningVisible)}
            header={getI18n('general.closeModal')}
            onOk={onCancel}
            okText={getI18n('general.antd.Uploader.ok')}
            cancelText={getI18n('general.cancel')}
            onCancel={hideWarning}>
            {getI18n('general.uploadWarning')}
          </Warning>
        </div>
      </div>
    </Modal>
  );
}

export default UploadModal;
